import * as React from "react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface MobileSheetProps {
  /**
   * Controls the open/close state
   */
  open: boolean;
  /**
   * Callback when the sheet is opened or closed
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Sheet title (displayed in header)
   */
  title: string;
  /**
   * Optional description (displayed under title)
   */
  description?: string;
  /**
   * Sheet content (form fields, etc.)
   */
  children: React.ReactNode;
  /**
   * Optional footer actions (Cancel, Save, etc.)
   */
  footer?: React.ReactNode;
  /**
   * Custom className for sheet content
   */
  className?: string;
  /**
   * Max height of the sheet (default: 85dvh)
   */
  maxHeight?: string;
}

/**
 * MobileSheet - Golden Reference Pattern (based on Quick Actions popup)
 * 
 * Features:
 * - Bottom slide-up animation (Bigin-style)
 * - Rounded top corners (rounded-t-2xl)
 * - Compact header (title + X in same row)
 * - Scrollable body with proper safe-area
 * - Sticky footer with full-width buttons
 * - Safe-area aware (iOS notch)
 * - Desktop fallback: centered dialog
 * 
 * Usage example:
 * MobileSheet with open, onOpenChange, title, description, footer props
 * Footer contains Button elements for Cancel and Save actions
 * Children contain form fields in a space-y-4 container
 */
export function MobileSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
  maxHeight = "85dvh",
}: MobileSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          "rounded-t-2xl p-0 gap-0 flex flex-col",
          "pb-[env(safe-area-inset-bottom)] max-h-[85dvh]",
          className
        )}
        style={{ maxHeight }}
        hideClose={true}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header: Title + X (same row), description below */}
        <div className="flex items-start justify-between flex-shrink-0 px-6 pt-4 pb-2 gap-3 border-b">
          <div className="flex-1 min-w-0">
            <SheetTitle className="text-left text-lg font-semibold truncate">{title}</SheetTitle>
            {description && (
              <SheetDescription className="text-left text-sm mt-0.5">{description}</SheetDescription>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="flex-shrink-0 h-8 w-8 rounded-full"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>

        {/* Scrollable body */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-6 py-4 min-h-0"
          style={{ maxHeight: `calc(${maxHeight} - 180px)` }}
        >
          {children}
        </div>

        {/* Footer (sticky at bottom, above safe-area) */}
        {footer && (
          <div className="flex-shrink-0 pt-4 px-6 pb-4 border-t flex gap-2 flex-col-reverse sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * MobileSheetFooterButton - Standardized button for mobile sheets
 * 
 * Features:
 * - Full-width on mobile
 * - Minimum 48px height (touch target)
 * - Consistent spacing
 */
export function MobileSheetFooterButton({
  children,
  variant = "default",
  onClick,
  disabled,
  className,
}: {
  children: React.ReactNode;
  variant?: "default" | "outline" | "destructive";
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex-1 sm:flex-initial min-h-[48px]",
        className
      )}
    >
      {children}
    </Button>
  );
}
